import { Component, inject, signal } from '@angular/core';
import { NgForm } from '@angular/forms';
import { AppRoutes } from 'src/app/core/models/routes.model';
import { BaseComponent } from 'src/app/core/components/base-component/base-component.component';
import { EcommerceAuthService } from 'src/app/core/services/ecommerce/ecommerce-auth.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { extractApiErrorDetail } from 'src/app/core/utils/http/api-error.util';

type ConfirmEmailState = 'verifying' | 'confirmed' | 'failed' | 'missing-token';

/**
 * Rotta pubblica `/confirm-email?token=...`: è l'indirizzo del link che Odoo
 * invia per verificare l'email dell'account ecommerce. Sta al primo livello
 * delle rotte perché il link arriva sul dominio nudo, e non ha guard: chi la
 * apre non ha ancora una sessione.
 *
 * `POST /auth/confirm-email` non rilascia una sessione, quindi la pagina
 * prosegue con il login e porta all'area acquisti. L'accesso è quello
 * Keycloak, lo stesso del bottone "Accedi" della topbar: dal 15/09/2026 è
 * l'unica via, quindi qui non c'è più il form legacy con email e password.
 */
@Component({
  selector: 'app-confirm-email',
  templateUrl: './confirm-email.component.html',
  styleUrls: ['./confirm-email.component.scss'],
  standalone: false,
})
export class ConfirmEmailComponent extends BaseComponent {
  private readonly ecommerceAuthService = inject(EcommerceAuthService);
  private readonly authService = inject(AuthService);

  readonly AppRoutes = AppRoutes;
  readonly state = signal<ConfirmEmailState>('verifying');
  /** Testo restituito dal backend, preferito ai nostri messaggi di default. */
  readonly message = signal<string>('');

  resendEmail = '';
  isSubmitting = false;
  resendError: string | null = null;

  /** Il token è monouso: una seconda POST fallirebbe su un account già valido. */
  private hasConfirmed = false;

  override ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const token = params.get('token')?.trim() ?? '';

    // Se il link porta anche l'email, non la si fa ridigitare nel reinvio.
    this.resendEmail = params.get('email')?.trim() ?? '';

    if (!token) {
      this.state.set('missing-token');
    } else {
      void this.confirm(token);
    }

    super.ngOnInit();
  }

  /** Accesso Keycloak: al ritorno si atterra sull'area acquisti. */
  async onKeycloakLogin(): Promise<void> {
    await this.authService.login(this.purchasesUrl);
  }

  /** Link scaduto o già usato: si richiede una nuova email di conferma. */
  async onResend(form: NgForm): Promise<void> {
    if (form.invalid || this.isSubmitting) {
      form.control.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.resendError = null;
    try {
      const response = await this.ecommerceAuthService.resendConfirmation(
        this.resendEmail.trim()
      );
      this.toast.add({
        severity: 'success',
        summary: 'Email inviata',
        detail: response.detail,
      });
    } catch (error) {
      this.resendError = extractApiErrorDetail(
        error,
        "Non è stato possibile inviare una nuova email di conferma. Riprova più tardi."
      );
    } finally {
      this.isSubmitting = false;
    }
  }

  goToPlans(): void {
    void this.router.navigateByUrl(
      `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${AppRoutes.DEFAULT_APP_CODE}/${AppRoutes.DASHBOARD}`
    );
  }

  private async confirm(token: string): Promise<void> {
    if (this.hasConfirmed) {
      return;
    }
    this.hasConfirmed = true;

    try {
      const response = await this.ecommerceAuthService.confirmEmail(token);
      this.message.set(response.detail || 'Il tuo indirizzo email è stato confermato.');
      this.state.set('confirmed');
    } catch (error) {
      this.message.set(
        extractApiErrorDetail(
          error,
          'Il link di conferma non è valido o è scaduto. Richiedine uno nuovo.'
        )
      );
      this.state.set('failed');
    }
  }

  private get purchasesUrl(): string {
    return `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${AppRoutes.DEFAULT_APP_CODE}/${AppRoutes.ACQUISTI}`;
  }
}
